import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui';
import { Skeleton } from '@/components/ui';
import { formatDate } from '@/lib/utils';
import type { TimelineEntry, TransactionStatus } from '@/types/admin-transaction';

// Status → Badge-Variante (gleiche Logik wie TransactionStatusBadge)
function getStatusVariant(status: string) {
  switch (status as TransactionStatus) {
    case 'charged':
    case 'renewed':
      return 'success';
    case 'identified':
    case 'setup_complete':
      return 'info';
    case 'failed':
      return 'destructive';
    case 'pending':
    case 'awaiting_identification':
    case 'awaiting_consent':
      return 'warning';
    case 'cancelled':
    case 'expired':
      return 'outline';
    default:
      return 'secondary';
  }
}

// Typ-spezifische Labels und Farben
const TYPE_CONFIG = {
  phase: { label: 'Status', className: 'border-blue-300 bg-blue-50 text-blue-700' },
  report: { label: 'Report', className: 'border-amber-300 bg-amber-50 text-amber-700' },
  notification: { label: 'Notification', className: 'border-green-300 bg-green-50 text-green-700' },
} as const;

function CollapsiblePayload({ payload }: { payload: string }) {
  const { t } = useTranslation('transactions');
  const [open, setOpen] = useState(false);

  let formatted = payload;
  try {
    formatted = JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    // Kein valides JSON — als-is anzeigen
  }

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="text-xs text-muted-foreground underline hover:no-underline"
      >
        {open ? t('timeline.payloadHide') : t('timeline.payloadShow')}
      </button>
      {open && (
        <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted p-2 text-xs">
          {formatted}
        </pre>
      )}
    </div>
  );
}

function PhaseEvent({ entry }: { entry: TimelineEntry }) {
  return (
    <div className="flex items-center gap-2">
      {entry.status && (
        <Badge variant={getStatusVariant(entry.status)}>{entry.status}</Badge>
      )}
      {entry.trigger && (
        <span className="text-sm text-muted-foreground">{entry.trigger}</span>
      )}
    </div>
  );
}

function ReportEvent({ entry }: { entry: TimelineEntry }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-sm">
        <span className="font-medium">{entry.reportType ?? 'Report'}</span>
        {entry.statusCode != null && (
          <Badge variant={entry.statusCode === 0 ? 'success' : 'destructive'}>
            Code: {entry.statusCode}
          </Badge>
        )}
        {entry.operatorId && (
          <span className="text-muted-foreground">{entry.operatorId}</span>
        )}
      </div>
      {entry.rawPayload && <CollapsiblePayload payload={entry.rawPayload} />}
    </div>
  );
}

function NotificationEvent({ entry }: { entry: TimelineEntry }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="font-medium">{entry.event ?? 'Notification'}</span>
      {entry.httpStatus != null && (
        <Badge variant={entry.httpStatus >= 200 && entry.httpStatus < 300 ? 'success' : 'destructive'}>
          HTTP {entry.httpStatus}
        </Badge>
      )}
      {entry.latencyMs != null && (
        <span className="text-muted-foreground">{entry.latencyMs}ms</span>
      )}
      {entry.notificationUrl && (
        <span className="truncate text-xs text-muted-foreground" title={entry.notificationUrl}>
          {entry.notificationUrl}
        </span>
      )}
    </div>
  );
}

function TimelineItem({ entry }: { entry: TimelineEntry }) {
  const config = TYPE_CONFIG[entry.type] ?? TYPE_CONFIG.phase;

  return (
    <div className="flex gap-3 border-l-2 border-border pb-4 pl-4 last:pb-0">
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${config.className}`}>
            {config.label}
          </span>
          <time className="text-xs text-muted-foreground">{formatDate(entry.timestamp)}</time>
        </div>
        {entry.type === 'phase' && <PhaseEvent entry={entry} />}
        {entry.type === 'report' && <ReportEvent entry={entry} />}
        {entry.type === 'notification' && <NotificationEvent entry={entry} />}
      </div>
    </div>
  );
}

export interface TransactionTimelineProps {
  timeline: TimelineEntry[];
  isLoading?: boolean;
}

export function TransactionTimeline({ timeline, isLoading }: TransactionTimelineProps) {
  const { t } = useTranslation('transactions');
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3 border-l-2 border-border pb-4 pl-4">
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (timeline.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">
        {t('timeline.empty')}
      </p>
    );
  }

  return (
    <div className="space-y-0" data-testid="transaction-timeline">
      {timeline.map((entry, idx) => (
        <TimelineItem key={`${entry.timestamp}-${entry.type}-${idx}`} entry={entry} />
      ))}
    </div>
  );
}
